import React, { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Generic scroll-position indicator strip for Swarm and Workflow modes.
 *
 * Reads elements inside containerRef that have a `data-indicator` attribute,
 * maps each type to a colour, and renders a clickable tick that scrolls to
 * that element.
 *
 * Colour map:
 *   human       – blue   (#636cff) – initial question / human bubble
 *   intervention– cyan   (#17a2b8) – user intervention in swarm
 *   summary     – purple (#9b59b6) – swarm summary
 *   input       – blue   (#636cff) – workflow user input
 *   output      – green  (#28a745) – workflow completed output
 */
const INDICATOR_COLORS = {
  human:        { solid: '#636cff',  faded: 'rgba(99,108,255,0.35)' },
  intervention: { solid: '#17a2b8',  faded: 'rgba(23,162,184,0.35)' },
  summary:      { solid: '#9b59b6',  faded: 'rgba(155,89,182,0.35)' },
  input:        { solid: '#636cff',  faded: 'rgba(99,108,255,0.35)' },
  output:       { solid: '#28a745',  faded: 'rgba(40,167,69,0.35)'  },
};

const INDICATOR_LABELS = {
  human:        'Initial question — click to jump',
  intervention: 'User intervention — click to jump',
  summary:      'Summary — click to jump',
  input:        'User input — click to jump',
  output:       'Workflow output — click to jump',
};

const ModeScrollStrip = ({ containerRef }) => {
  const [ticks, setTicks] = useState([]);
  const rafRef   = useRef(null);
  const moTimer  = useRef(null);

  const measure = useCallback(() => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    rafRef.current = requestAnimationFrame(() => {
      rafRef.current = null;
      const container = containerRef.current;
      if (!container || container.scrollHeight === 0) return;

      const totalHeight = container.scrollHeight;
      const elements = Array.from(container.querySelectorAll('[data-indicator]'));

      const newTicks = elements.map((el, i) => ({
        key: i,
        type: el.getAttribute('data-indicator'),
        position: el.offsetTop / totalHeight,
        elementKey: el.getAttribute('data-indicator-key') || String(i),
      }));

      setTicks(newTicks);
    });
  }, [containerRef]);

  useEffect(() => {
    measure();
    return () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); };
  }, [measure]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const ro = new ResizeObserver(measure);
    ro.observe(container);

    const handleMutation = () => {
      if (moTimer.current) return;
      moTimer.current = setTimeout(() => { moTimer.current = null; measure(); }, 200);
    };
    const mo = new MutationObserver(handleMutation);
    mo.observe(container, { childList: true, subtree: true });

    return () => {
      ro.disconnect();
      mo.disconnect();
      if (moTimer.current) { clearTimeout(moTimer.current); moTimer.current = null; }
    };
  }, [containerRef, measure]);

  const scrollTo = useCallback((type, key) => {
    const container = containerRef.current;
    if (!container) return;
    const el = container.querySelector(
      key ? `[data-indicator-key="${key}"]` : `[data-indicator="${type}"]`
    );
    if (el) container.scrollTo({ top: Math.max(0, el.offsetTop - 16), behavior: 'smooth' });
  }, [containerRef]);

  if (ticks.length === 0) return null;

  return (
    <div
      style={{
        width: 8,
        flexShrink: 0,
        position: 'relative',
        borderLeft: '1px solid var(--border-color, rgba(0,0,0,0.08))',
        userSelect: 'none',
      }}
      aria-hidden="true"
    >
      {ticks.map((tick) => {
        const colors = INDICATOR_COLORS[tick.type] || INDICATOR_COLORS.input;
        const label  = INDICATOR_LABELS[tick.type] || 'Click to jump';
        return (
          <div
            key={tick.key}
            title={label}
            style={{
              position: 'absolute',
              top: `${tick.position * 100}%`,
              left: 2,
              width: 4,
              height: 6,
              borderRadius: 2,
              backgroundColor: colors.solid,
              transform: 'translateY(-50%)',
              cursor: 'pointer',
              transition: 'filter 0.15s',
            }}
            onClick={(e) => { e.stopPropagation(); scrollTo(tick.type, tick.elementKey); }}
            onMouseEnter={(e) => { e.currentTarget.style.filter = 'brightness(1.4)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.filter = 'none'; }}
          />
        );
      })}
    </div>
  );
};

export default ModeScrollStrip;
